import React, { useEffect, useState, useRef } from 'react';
import { useInbox } from '@trycourier/react-hooks';
import { Bell, Check, Archive, MoreVertical, X, Sparkles, Clock, User, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface CustomInboxProps {
  showUnreadMessageCount?: boolean;
}

const CustomInbox: React.FC<CustomInboxProps> = ({ showUnreadMessageCount = true }) => {
  const inbox = useInbox();
  const [isOpen, setIsOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const prevUnreadCount = useRef<number>(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Create audio element
    audioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');
    audioRef.current.preload = 'auto';
  }, []);

  useEffect(() => {
    const currentUnreadCount = inbox.messages?.filter(message => !message.read).length || 0;
    if (currentUnreadCount > prevUnreadCount.current && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => {
        console.warn('Failed to play notification sound:', err);
      });
    }
    prevUnreadCount.current = currentUnreadCount;
  }, [inbox.messages?.length]);

  const unreadCount = inbox.messages?.filter(message => !message.read).length || 0;

  const handleReadMessage = (message: any) => (event: React.MouseEvent) => {
    event.preventDefault();
    inbox.markMessageRead(
      message.messageId,
      message.trackingIds.readTrackingId
    );
  };

  const handleUnreadMessage = (message: any) => (event: React.MouseEvent) => {
    event.preventDefault();
    inbox.markMessageUnread(message.messageId);
  };

  const handleArchiveMessage = (message: any) => (event: React.MouseEvent) => {
    event.preventDefault();
    inbox.markMessageArchived(message.messageId);
  };

  const handleTicketClick = (message: any) => (event: React.MouseEvent) => {
    event.preventDefault();
    console.log(message);

    setIsOpen(false);

    if (message?.data?.type === "pullse_ticket_created" && message?.data?.ticketId) {
      const url = `/home/inbox/all?ticketId=${message.data.ticketId}`;
      if (url) {
        navigate(url);
      }
    }
  };

  const markAllAsRead = () => {
    inbox.messages
      ?.filter(message => !message.read)
      ?.forEach(message => {
        inbox.markMessageRead(
          message.messageId,
          // message.trackingIds.readTrackingId
        );
        // Archive the message
        inbox.markMessageArchived(message.messageId);
      });
  };

  const getNotificationIcon = (message: any) => {
    // You can customize this based on message type
    if (message.type === 'user_mention') return <User className="h-4 w-4" />;
    if (message.type === 'system') return <Sparkles className="h-4 w-4" />;
    return <Bell className="h-4 w-4" />;
  };

  const getNotificationColor = (message: any) => {
    if (message.type === 'urgent') return 'bg-red-500';
    if (message.type === 'success') return 'bg-emerald-500';
    if (message.type === 'warning') return 'bg-amber-500';
    return 'bg-blue-500';
  };

  const handleSettingsClick = (event: React.MouseEvent) => {
    event.preventDefault();
    navigate('/home/settings/email-templates');
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "relative p-2.5 rounded-xl transition-all duration-300 group",
              "hover:bg-white/10 hover:shadow-lg hover:shadow-blue-500/20",
              "text-gray-600 hover:text-gray-800 border border-transparent",
              "hover:border-white/20 hover:scale-105",
              unreadCount > 0 && ""
            )}
          >
            <Bell className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
            {showUnreadMessageCount && unreadCount > 0 && (
              <Badge
                variant="destructive"
                className={cn(
                  "absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs font-bold",
                  "bg-gradient-to-r from-red-500 to-pink-500 border-2 border-white",
                  "animate-scale-in shadow-lg shadow-red-500/30"
                )}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "w-80 p-0 bg-white/5 backdrop-blur-3xl border border-white/10 shadow-2xl shadow-black/20",
            "rounded-2xl animate-scale-in overflow-hidden",
            "before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/10 before:via-white/5 before:to-transparent before:rounded-2xl before:pointer-events-none",
            "after:absolute after:inset-[1px] after:bg-gradient-to-br after:from-transparent after:via-white/5 after:to-white/10 after:rounded-2xl after:pointer-events-none"
          )}
          align="end"
          sideOffset={8}
        >
          {/* Compact Header */}
          <div className="relative bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-sm p-3 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center justify-center flex-row gap-2">
                  <Bell className="h-4 w-4 text-foreground/80" />
                  <h3 className="font-semibold text-sm text-foreground">Notifications</h3>
                  {unreadCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="h-5 px-1.5 text-xs bg-primary/20 text-primary border-0 font-medium"
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </div>
                <>
                  <Settings className="h-4 w-4 text-foreground/80 cursor-pointer" onClick={handleSettingsClick} />
                </>
              </div>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="h-6 px-2 text-xs text-foreground/70 hover:text-foreground hover:bg-white/10 rounded-md"
                >
                  Clear all
                </Button>
              )}
            </div>
          </div>

          {/* Notifications Content with Scroll */}
          <div className="max-h-80 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="max-h-80">
                {!inbox.messages || inbox.messages.length === 0 ? (
                  <div className="p-6 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                      <Bell className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground font-medium">No notifications</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {inbox.messages?.map((message, index) => (
                      <div
                        key={message.messageId}
                        className={cn(
                          "relative p-3 transition-all duration-300 hover:bg-white/5 group cursor-pointer backdrop-blur-sm",
                          "animate-fade-in",
                          !message.read && "bg-white/5 border-l-2 border-l-primary/50"
                        )}
                        style={{ animationDelay: `${index * 30}ms` }}
                        onClick={handleTicketClick(message)}
                      >
                        <div className="flex items-start gap-3">
                          {/* Compact Icon */}
                          <div className={cn(
                            "flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-md transition-transform duration-200 group-hover:scale-105",
                            getNotificationColor(message)
                          )}>
                            {getNotificationIcon(message)}
                          </div>

                          {/* Compact Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <p className={cn(
                                  "text-sm leading-tight mb-1",
                                  !message.read ? 'font-semibold text-foreground' : 'text-foreground/80'
                                )}>
                                  {message.title || 'Notification'}
                                </p>
                                {message.preview && (
                                  <p className="text-xs text-muted-foreground mb-2 leading-relaxed">
                                    {message.preview}
                                  </p>
                                )}
                                <div className="flex items-center gap-3">
                                  <p className="text-xs text-muted-foreground">
                                    {formatDistanceToNow(new Date(message.created), { addSuffix: true })}
                                  </p>
                                  {!message.read && (
                                    <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                                  )}
                                </div>
                              </div>

                              {/* Compact Actions */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white/10"
                                  >
                                    <MoreVertical className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="w-40 bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl rounded-xl p-1"
                                >
                                  {message.read ? (
                                    <DropdownMenuItem
                                      onClick={handleUnreadMessage(message)}
                                      className="text-xs rounded-lg hover:bg-white/10 transition-colors duration-200"
                                    >
                                      <X className="h-3 w-3 mr-2" />
                                      Mark unread
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem
                                      onClick={handleReadMessage(message)}
                                      className="text-xs rounded-lg hover:bg-white/10 transition-colors duration-200"
                                    >
                                      <Check className="h-3 w-3 mr-2" />
                                      Mark read
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={handleArchiveMessage(message)}
                                    className="text-xs rounded-lg hover:bg-white/10 transition-colors duration-200"
                                  >
                                    <Archive className="h-3 w-3 mr-2" />
                                    Archive
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </PopoverContent>
      </Popover>

      {/* TEMPORARY: CopilotKit Debug Panel for testing */}
      {/* <div className="fixed bottom-4 right-4 z-50">
        <details className="bg-white border border-gray-200 rounded-lg shadow-lg">
          <summary className="p-3 cursor-pointer font-medium text-sm bg-green-50 border-b border-gray-200 rounded-t-lg">
            🧪 CopilotKit Debug Panel (Click to Open)
          </summary>
          <div className="p-4 max-w-4xl max-h-96 overflow-auto">
            <CopilotDebugPanel />
          </div>
        </details>
      </div> */}
    </>
  );
};

export default CustomInbox;


